import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { 
  ArrowLeft, MapPin, Clock, Briefcase, GraduationCap,
  ChevronRight, Upload, Send, Building2, Users, TrendingUp,
  CheckCircle, Star
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { getJobOpenings, submitJobApplication, supabase } from "@/lib/api";

const benefits = [
  {
    icon: TrendingUp,
    title: "Career Growth",
    description: "Clear career progression paths with regular promotions and skill development opportunities."
  },
  {
    icon: GraduationCap,
    title: "Learning & Development",
    description: "Continuous learning through workshops, certifications, and on-the-job training."
  },
  {
    icon: Users,
    title: "Collaborative Culture",
    description: "Work with experienced professionals in a supportive and team-oriented environment."
  },
  {
    icon: Building2,
    title: "Diverse Exposure",
    description: "Gain experience across multiple industries and service lines."
  }
];

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
};

const JoinUsPage = () => {
  const [selectedJob, setSelectedJob] = useState(null);
  const [showApplicationForm, setShowApplicationForm] = useState(false);
  const [jobOpenings, setJobOpenings] = useState([]);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    position: "",
    experience: "",
    currentCompany: "",
    message: ""
  });
  const [loading, setLoading] = useState(false);
  const [resumeFile, setResumeFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    const loadJobOpenings = async () => {
      try {
        const response = await getJobOpenings();

        if (Array.isArray(response) && response.length) {
          setJobOpenings(response);
          return;
        }

        setJobOpenings([]);
      } catch (error) {
        setJobOpenings([]);
      }
    };

    loadJobOpenings();
  }, []);

  const handleApply = (job) => {
    setSelectedJob(job);
    setFormData({ ...formData, position: job.title });
    setShowApplicationForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.email || !formData.phone || !formData.position || !formData.experience || !formData.message) {
      toast.error("Please complete all required fields before submitting.");
      return;
    }

    setLoading(true);

    let resumeUrl = null;

    if (resumeFile) {
      setUploading(true);
      try {
        const fileExt = resumeFile.name.split('.').pop();
        const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;
        const filePath = `${formData.position}/${fileName}`;

        const { error: uploadError } = await supabase.storage
          .from('resumes')
          .upload(filePath, resumeFile, {
            contentType: resumeFile.type,
            cacheControl: '3600',
          });

        if (uploadError) throw uploadError;

        const { data: urlData } = supabase.storage
          .from('resumes')
          .getPublicUrl(filePath);

        resumeUrl = urlData.publicUrl;
      } catch (error) {
        toast.error("Failed to upload resume. Please try again.");
        setLoading(false);
        setUploading(false);
        return;
      } finally {
        setUploading(false);
      }
    }
    
    try {
      await submitJobApplication({
        full_name: formData.name,
        email: formData.email,
        phone: formData.phone,
        position: formData.position,
        experience: formData.experience,
        current_company: formData.currentCompany || undefined,
        cover_letter: formData.message,
        resume_url: resumeUrl,
      });
      toast.success("Application submitted successfully! We'll get back to you soon.");
      setFormData({ name: "", email: "", phone: "", position: "", experience: "", currentCompany: "", message: "" });
      setResumeFile(null);
      setShowApplicationForm(false);
      setSelectedJob(null);
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative bg-[#0B1120] pt-32 pb-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Link 
            to="/" 
            className="inline-flex items-center text-white/70 hover:text-[#C5A059] mb-8 transition-colors"
          >
            <ArrowLeft size={18} className="mr-2" />
            Back to Home
          </Link>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-16 h-1 bg-[#C5A059]" />
              <span className="text-[#C5A059] font-medium text-sm uppercase tracking-widest">
                Careers
              </span>
            </div>
            
            <h1 className="logo-font text-4xl sm:text-5xl lg:text-6xl text-white font-bold mb-6">
              Join <span className="text-[#C5A059]">SSA</span>
            </h1>
            
            <p className="text-white/70 text-lg max-w-2xl">
              Build your career with one of India's leading Chartered Accountancy firms. 
              Discover opportunities that match your passion and expertise.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Application Form (shown when applying) */}
      {showApplicationForm && selectedJob && (
        <section className="py-16 bg-[#C5A059]/10 border-b border-[#C5A059]/20">
          <div className="max-w-3xl mx-auto px-6 md:px-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h2 className="font-serif text-2xl font-bold text-[#0B1120] mb-2">
                    Apply for: {selectedJob.title}
                  </h2>
                  <p className="text-slate-600">
                    {selectedJob.location} • {selectedJob.type}
                  </p>
                </div>
                <button 
                  onClick={() => { setShowApplicationForm(false); setSelectedJob(null); }}
                  className="text-slate-500 hover:text-[#0B1120]"
                >
                  ✕ Close
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Full Name *</label>
                    <Input
                      data-testid="career-name-input"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      className="h-12"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Email *</label>
                    <Input
                      data-testid="career-email-input"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                      className="h-12"
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Phone *</label>
                    <Input
                      data-testid="career-phone-input"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      required
                      className="h-12"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Years of Experience</label>
                    <Select 
                      value={formData.experience} 
                      onValueChange={(value) => setFormData({ ...formData, experience: value })}
                    >
                      <SelectTrigger data-testid="career-experience-select" className="h-12">
                        <SelectValue placeholder="Select experience" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Fresher">Fresher</SelectItem>
                        <SelectItem value="0-1 Years">0-1 Years</SelectItem>
                        <SelectItem value="1-3 Years">1-3 Years</SelectItem>
                        <SelectItem value="3-5 Years">3-5 Years</SelectItem>
                        <SelectItem value="5-10 Years">5-10 Years</SelectItem>
                        <SelectItem value="10+ Years">10+ Years</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Current Company (if any)</label>
                  <Input
                    data-testid="career-company-input"
                    value={formData.currentCompany}
                    onChange={(e) => setFormData({ ...formData, currentCompany: e.target.value })}
                    className="h-12"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Resume (PDF, DOC, DOCX)</label>
                  <label className="flex items-center justify-center w-full h-12 px-4 border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:border-[#C5A059]/50 hover:bg-[#C5A059]/5 transition-all">
                    <input
                      type="file"
                      accept=".pdf,.doc,.docx"
                      onChange={(e) => setResumeFile(e.target.files[0])}
                      className="hidden"
                    />
                    <div className="flex items-center gap-2 text-slate-600">
                      <Upload size={16} />
                      {resumeFile ? (
                        <span className="text-sm font-medium text-[#0B1120]">{resumeFile.name}</span>
                      ) : (
                        <span className="text-sm">Choose a file</span>
                      )}
                    </div>
                  </label>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Cover Letter / Message *</label>
                  <Textarea
                    data-testid="career-message-input"
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    required
                    rows={5}
                    placeholder="Tell us about yourself and why you're interested in this position..."
                    className="resize-none"
                  />
                </div>

                <Button 
                  data-testid="career-submit-btn"
                  type="submit"
                  disabled={loading || uploading}
                  className="w-full sm:w-auto bg-[#0B1120] hover:bg-[#C5A059] text-white px-8 py-6"
                >
                  {loading || uploading ? (
                    <>
                      <span className="spinner mr-2" />
                      {uploading ? "Uploading Resume..." : "Submitting..."}
                    </>
                  ) : (
                    <>
                      Submit Application
                      <Send size={18} className="ml-2" />
                    </>
                  )}
                </Button>
              </form>
            </motion.div>
          </div>
        </section>
      )}

      {/* Why Join SSA */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-[#0B1120] mb-4">
              Why Join SSA?
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              At SSA, we believe in nurturing talent and providing opportunities for professional growth.
            </p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8"
          >
            {benefits.map((benefit) => (
              <motion.div
                key={benefit.title}
                variants={fadeInUp}
                className="text-center p-6"
              >
                <div className="w-16 h-16 rounded-full bg-[#C5A059]/10 flex items-center justify-center mx-auto mb-4">
                  <benefit.icon size={28} className="text-[#C5A059]" />
                </div>
                <h3 className="font-serif text-xl font-semibold text-[#0B1120] mb-2">
                  {benefit.title}
                </h3>
                <p className="text-slate-600 text-sm">
                  {benefit.description}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Current Openings */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-[#0B1120] mb-4">
              Current Openings
            </h2>
            <p className="text-slate-600">
              Explore opportunities that match your skills and career aspirations.
            </p>
          </motion.div>

          {jobOpenings.length === 0 ? (
            <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center">
              <h3 className="text-2xl font-semibold text-[#0B1120] mb-4">No job openings available</h3>
              <p className="text-slate-600">Current openings are being updated. Please check back soon.</p>
            </div>
          ) : (
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={staggerContainer}
              className="space-y-6"
            >
              {jobOpenings.map((job, index) => {
                const openingRequirements = Array.isArray(job.requirements)
                  ? job.requirements
                  : typeof job.requirements === "string"
                    ? job.requirements
                        .split(/\r?\n/)
                        .map((item) => item.trim())
                        .filter(Boolean)
                    : [];

                const openingKey = job.id || job._id || index;

                return (
                <motion.div
                  key={openingKey}
                  variants={fadeInUp}
                  className="bg-white rounded-xl border border-slate-200 hover:border-[#C5A059]/30 hover:shadow-lg transition-all overflow-hidden"
                >
                  <div className="p-6 md:p-8">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
                      <div>
                        <h3 className="font-serif text-xl font-semibold text-[#0B1120] mb-2">
                          {job.title || "Untitled Role"}
                        </h3>
                        <div className="flex flex-wrap gap-3 text-sm text-slate-500">
                          <span className="flex items-center gap-1">
                            <Briefcase size={14} />
                            {job.department || "Department not specified"}
                          </span>
                          <span className="flex items-center gap-1">
                            <MapPin size={14} />
                            {job.location || "Location not specified"}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock size={14} />
                            {job.type || "Type not specified"}
                          </span>
                          <span className="flex items-center gap-1">
                            <Star size={14} />
                            {job.experience || "Experience not specified"}
                          </span>
                        </div>
                      </div>
                      <Button 
                        data-testid={`apply-btn-${openingKey}`}
                        onClick={() => handleApply(job)}
                        className="bg-[#0B1120] hover:bg-[#C5A059] text-white whitespace-nowrap"
                      >
                        Apply Now
                        <ChevronRight size={16} className="ml-1" />
                      </Button>
                    </div>

                    <p className="text-slate-600 mb-4">
                      {job.description || "No description available."}
                    </p>

                    <div>
                      <h4 className="text-sm font-semibold text-[#0B1120] mb-2">Requirements:</h4>
                      <ul className="grid sm:grid-cols-2 gap-2">
                        {openingRequirements.length > 0 ? (
                          openingRequirements.map((req, idx) => (
                            <li key={idx} className="flex items-start gap-2 text-sm text-slate-600">
                              <CheckCircle size={16} className="text-[#C5A059] shrink-0 mt-0.5" />
                              {req}
                            </li>
                          ))
                        ) : (
                          <li className="text-sm text-slate-500">No requirements listed.</li>
                        )}
                      </ul>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
          )}
        </div>
      </section>

      {/* General Application CTA */}
      <section className="py-20 bg-[#0B1120]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-white mb-4">
              Don't See a Suitable Opening?
            </h2>
            <p className="text-white/70 mb-8 max-w-xl mx-auto">
              We're always interested in hearing from talented professionals. 
              Send us your resume and we'll keep you in mind for future opportunities.
            </p>
            <Link to="/#contact">
              <Button className="bg-[#C5A059] hover:bg-white hover:text-[#0B1120] text-white px-8 py-6">
                Send General Application
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Meet the Team CTA */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <h3 className="font-serif text-2xl font-bold text-[#0B1120] mb-4">
            Want to Know More About Our Team?
          </h3>
          <p className="text-slate-600 mb-6">
            Meet the professionals you'll be working with.
          </p>
          <Link to="/our-people">
            <Button variant="outline" className="border-[#0B1120] text-[#0B1120] hover:bg-[#0B1120] hover:text-white px-8 py-6">
              Meet Our People
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default JoinUsPage;
